/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Select, type SelectProps } from 'antd';
import type { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import isUndefined from 'lodash/isUndefined';
import { normaliseString } from 'pos-shared/utils/textHelpers';

import { extractPosFormItemProps } from './FormUtils.ts';
import {
  PosFormItem,
  type PosFormItemControlledComponentProps,
  type PosFormItemUncontrolledComponentProps,
} from './PosFormItem.tsx';

type BasePosSelectProps<
  ValueType = any,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> = Omit<SelectProps<ValueType, OptionType>, 'value' | 'name'>;

type PosSelectControlled<
  TFieldValues extends FieldValues = FieldValues,
  ValueType = any,
  OptionType extends BaseOptionType = DefaultOptionType,
> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  onChange?: never;
  value?: never;
} & PosFormItemControlledComponentProps<TFieldValues> &
  Omit<BasePosSelectProps<ValueType, OptionType>, 'onChange'>;

type PosSelectUncontrolled<
  ValueType = any,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> = {
  control?: never;
  name?: never;
  onChange?: BasePosSelectProps<ValueType, OptionType>['onChange'];
  value?: ValueType;
} & PosFormItemUncontrolledComponentProps &
  BasePosSelectProps<ValueType, OptionType>;

export type PosSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  ValueType = any,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> =
  | PosSelectControlled<TFieldValues, ValueType, OptionType>
  | PosSelectUncontrolled<ValueType, OptionType>;

/**
 * @component
 * @description Select dropdown with React-Hook-Form integration
 * @see {@link https://ant.design/components/select | AntD Select Component}
 *
 * @param PosSelectProps.control Control field from React-Hook-Form useForm
 * @remarks If an invalid control from useForm is passed then the Input will behave as an uncontrolled component and field name will not be type-safe
 * @param PosSelectProps.name Name of form field
 * @example
 * ```tsx
 * // Controlled (RHF) usage:
 * const { t } = useTranslation();
 * const { control } = useForm<FinanceForm>();
 *
 * const currencyOptions = [{
 *   key: 'gbp',
 *   label: 'GBP (£)',
 *   value: 'gbp'
 * }];
 *
 * <PosSelect
 *   control={control}
 *   name="currency"
 *   label={t('common.currency.currencyCode')}
 *   options={currencyOptions}
 * />
 *
 * // Uncontrolled (No RHF) usage:
 * type ExampleOption = {
 *    label: string;
 *    value: string;
 * };
 * const [currency, setCurrency] = useState<ExampleOption>();
 *
 * <PosSelect
 *   onChange={setCurrency}
 *   options={currencyOptions}
 *   value={currency}
 * />
 * ```
 */
export function PosSelect<
  TFieldValues extends FieldValues,
  ValueType,
  OptionType extends BaseOptionType,
>(props: PosSelectControlled<TFieldValues, ValueType, OptionType>): JSX.Element;

// Uncontrolled‐mode overload
export function PosSelect<ValueType, OptionType extends BaseOptionType>(
  props: PosSelectUncontrolled<ValueType, OptionType>
): JSX.Element;

export function PosSelect<
  TFieldValues extends FieldValues = FieldValues,
  ValueType = any,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
>({
  control,
  name,
  children,
  filterOption,
  ...props
}: PosSelectProps<TFieldValues, ValueType, OptionType>) {
  const isControlled = !!control;
  const { posFormItemProps, rest: selectProps } =
    extractPosFormItemProps(props);
  const ariaLabel = typeof props.label === 'string' ? props.label : name;

  const onControlledChange = (
    value: ValueType,
    _option: OptionType | OptionType[] | undefined,
    onChange: (...event: any[]) => void
  ) => {
    const controlledValue = isUndefined(value) ? null : value;
    onChange(controlledValue);
  };

  const onControlledClear = (onChange: (...event: any[]) => void) => {
    onChange(null);
    if (props.onClear) {
      props.onClear();
    }
  };

  const defaultFilterResults = (inputValue: string, option?: OptionType) =>
    normaliseString(option?.label ?? '').includes(normaliseString(inputValue));

  const resultsFilter = filterOption ? filterOption : defaultFilterResults;

  if (isControlled) {
    return (
      <PosFormItem control={control} name={name} {...posFormItemProps}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, ...field } }) => (
            <Select
              {...selectProps}
              {...field}
              filterOption={resultsFilter}
              onChange={(value, option) =>
                onControlledChange(value, option, onChange)
              }
              onClear={() => onControlledClear(onChange)}
              data-testid={name}
              aria-label={ariaLabel}
            />
          )}
        />
      </PosFormItem>
    );
  }

  return <Select filterOption={resultsFilter} {...props} />;
}
